/**
 * Inline rendering of the trigger sales-order detail for the suggestion modal's
 * middle panel. Same content the old SalesOrderDetailModal used to show — but
 * embedded directly in the modal instead of opened as a separate dialog when
 * the buyer clicked the SO badge in the cart-line table.
 */

import { T, useGT } from 'gt-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useSalesOrder } from 'src/api/rpc/hooks/accounts-receivable/useSalesOrders';
import { useReplenishmentForSalesOrder } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { CopyIdButton } from 'src/components/common/CopyIdButton';
import { MaxJoinsCell } from 'src/components/procurement/MaxJoinsCell';
import type { SalesOrderLineItem } from '@erp/rpc';

function formatDate(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

function formatDateTime(v: string | Date | null | undefined): string {
  if (!v) return '—';
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function formatMoney(v: string | null | undefined): string {
  if (v == null) return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

type LineAssignment = {
  lotLevelDeficit: boolean;
  selectedUnitNumber: string | null;
  fulfillment: 'cut' | 'ship_whole' | null;
  anchorSupplierName: string | null;
  deliveryWarehouseName: string | null;
};

function UnitAssignmentCell({
  ctx,
  quantity,
}: {
  ctx: LineAssignment | undefined;
  quantity: string | number | null | undefined;
}) {
  const gt = useGT();
  if (ctx?.lotLevelDeficit) {
    return (
      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
        <T context="so line lot-level deficit badge">not one piece</T>
      </span>
    );
  }
  if (ctx?.selectedUnitNumber == null) {
    return <span className="text-gray-300">—</span>;
  }
  return (
    <span className="font-mono text-xs">
      {ctx.fulfillment === 'cut'
        ? gt('cut {qty} from {unit}', {
            context: 'so line stock cut assignment',
            qty: quantity ?? '',
            unit: ctx.selectedUnitNumber,
          })
        : ctx.selectedUnitNumber}
    </span>
  );
}

function displayOrDash(v: string | number | null | undefined): string {
  if (v == null) return '—';
  return String(v);
}

function namedOrDash(name: string | null | undefined) {
  if (name == null) return <span className="text-gray-300">—</span>;
  return name;
}

function SalesOrderLineRow({
  lineItemNumber,
  sku,
  productName,
  quantity,
  unitPrice,
  lineTotal,
  isTrigger,
  ctx,
  salesOrderId,
  line,
}: {
  lineItemNumber: string | number | null | undefined;
  sku: string | null | undefined;
  productName: string | null | undefined;
  quantity: string | number | null | undefined;
  unitPrice: string | null | undefined;
  lineTotal: string | null | undefined;
  isTrigger: boolean;
  ctx: LineAssignment | undefined;
  salesOrderId: string;
  line: Pick<
    SalesOrderLineItem,
    | 'id'
    | 'maxJoins'
    | 'productDefaultMaxJoins'
    | 'demandArchetype'
    | 'millableStatus'
  >;
}) {
  return (
    <TableRow
      className={
        isTrigger
          ? 'bg-amber-100/70 border-l-4 border-l-amber-500 ring-1 ring-amber-300'
          : undefined
      }
    >
      <TableCell className="tabular-nums text-gray-500">
        {displayOrDash(lineItemNumber)}
      </TableCell>
      <TableCell className="font-mono text-xs">{displayOrDash(sku)}</TableCell>
      <TableCell className="text-sm">{displayOrDash(productName)}</TableCell>
      <TableCell className="text-right tabular-nums">
        {displayOrDash(quantity)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-gray-600">
        {formatMoney(unitPrice)}
      </TableCell>
      <TableCell className="text-right tabular-nums font-medium">
        {formatMoney(lineTotal)}
      </TableCell>
      <TableCell className="text-sm text-gray-700">
        {namedOrDash(ctx?.anchorSupplierName)}
      </TableCell>
      <TableCell className="text-sm text-gray-700">
        {namedOrDash(ctx?.deliveryWarehouseName)}
      </TableCell>
      <TableCell className="text-sm text-gray-700">
        <UnitAssignmentCell ctx={ctx} quantity={quantity} />
      </TableCell>
      <TableCell className="align-top">
        <MaxJoinsCell
          key={`${line.id}-${line.maxJoins ?? 'inherit'}`}
          salesOrderId={salesOrderId}
          line={line}
          compact
        />
      </TableCell>
    </TableRow>
  );
}

function mixedDestinationLabel(
  names: Array<string | null | undefined>,
  mixed: string,
): string | null {
  const distinct = [...new Set(names.filter((n): n is string => n != null))];
  if (distinct.length === 0) return null;
  if (distinct.length === 1) return distinct[0];
  return mixed;
}

interface Props {
  salesOrderId: string;
  // Highlight the line item that drove this suggestion (the rest are sibling
  // lines on the same SO, included for context).
  highlightLineItemId?: string | null;
}

export function SalesOrderInline({ salesOrderId, highlightLineItemId }: Props) {
  const gt = useGT();
  const { salesOrder, lineItems, isLoading, isError } = useSalesOrder(
    salesOrderId,
    Boolean(salesOrderId),
  );
  const { data: replenishmentData } = useReplenishmentForSalesOrder(
    salesOrderId,
    Boolean(salesOrderId),
  );

  const replenishmentByLineItemId = new Map(
    (replenishmentData?.items ?? []).map((it) => [
      it.triggerSalesOrderLineItemId,
      it,
    ]),
  );

  // DMDTRANS never populates the SO header total (`totalAmount` is null/$0 for
  // ~all imported orders), but each line carries unitPrice × qty in lineTotal.
  // Fall back to the summed line totals so the header reflects real value
  // instead of $0.
  const lineItemsTotal = (lineItems ?? []).reduce(
    (sum, li) => sum + (parseFloat(li.lineTotal ?? '0') || 0),
    0,
  );
  const headerTotal = parseFloat(salesOrder?.totalAmount ?? '0') || 0;
  const displayTotalUsesLines = headerTotal <= 0 && lineItemsTotal > 0;

  const destinationLabel = mixedDestinationLabel(
    (replenishmentData?.items ?? []).map((it) => it.deliveryWarehouseName),
    gt('Mixed', { context: 'sales order inline destination mixed' }),
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-sm text-gray-400">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        <T context="sales order inline loading">Loading sales order…</T>
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-amber-600">
        <T context="sales order inline load failed">
          Unable to load sales order.
        </T>
      </p>
    );
  }
  if (!salesOrder) {
    return (
      <p className="text-sm text-gray-400">
        <T context="sales order inline not found">Sales order not found.</T>
      </p>
    );
  }

  // AS400/DMDTRANS orders have no linked customer master record, so the
  // customer name + sold-to/ship-to codes only live in origin_metadata.
  // Prefer a linked customer record when one exists (non-Rexel sources).
  const originMetadata = salesOrder.originMetadata;
  const customerName =
    salesOrder.customer?.name ?? originMetadata?.customerName ?? null;

  return (
    // Single card wrapping the whole SO context — tinted light-green so the
    // tab content reads as a unified panel and matches the "must-act demand"
    // framing of SO triggers in the rest of the UI (SO badges, MOV-met cart
    // headers, etc.).
    <div className="space-y-3 rounded-lg border border-green-300 bg-green-100/70 p-4">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-800">
          <T context="sales order inline section heading">Sales Order</T> #
          {salesOrder.internalId}
        </h3>
        <CopyIdButton
          id={salesOrder.id}
          label={gt('Copy sales order ID', {
            context: 'copy id button label',
          })}
        />
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
          {salesOrder.status}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg border border-green-300 bg-white/80 p-3 text-sm">
        {customerName && (
          <div>
            <span className="text-gray-500">
              <T context="sales order inline customer label">Customer</T>
            </span>
            <p className="font-medium">{customerName}</p>
          </div>
        )}
        {originMetadata?.soldToCode && (
          <div>
            <span className="text-gray-500">
              <T context="sales order inline sold-to label">Sold-to</T>
            </span>
            <p className="font-mono text-xs font-medium">
              {originMetadata.soldToCode}
            </p>
          </div>
        )}
        {originMetadata?.shipToCode && (
          <div>
            <span className="text-gray-500">
              <T context="sales order inline ship-to label">Ship-to</T>
            </span>
            <p className="font-mono text-xs font-medium">
              {originMetadata.shipToCode}
            </p>
          </div>
        )}
        {salesOrder.customerPurchaseOrderNumber && (
          <div>
            <span className="text-gray-500">
              <T context="sales order inline po number label">Customer PO #</T>
            </span>
            <p className="font-medium">
              {salesOrder.customerPurchaseOrderNumber}
            </p>
          </div>
        )}
        <div>
          <span className="text-gray-500">
            <T context="sales order inline order date label">Order date</T>
          </span>
          <p className="font-medium">{formatDate(salesOrder.orderDate)}</p>
        </div>
        {salesOrder.deliveryDate && (
          <div>
            <span className="text-gray-500">
              <T context="sales order inline delivery date label">
                Delivery date
              </T>
            </span>
            <p className="font-medium">{formatDate(salesOrder.deliveryDate)}</p>
          </div>
        )}
        <div>
          <span className="text-gray-500">
            <T context="sales order inline total label">SO total</T>
          </span>
          <p className="font-medium text-green-700">
            {displayTotalUsesLines
              ? formatMoney(String(lineItemsTotal))
              : formatMoney(salesOrder.totalAmount)}
          </p>
          {displayTotalUsesLines && (
            <span className="text-[10px] text-gray-400">
              <T context="so total derived from summed line items">
                summed from lines
              </T>
            </span>
          )}
        </div>
        <div>
          <span className="text-gray-500">
            <T context="sales order inline destination label">Destination</T>
          </span>
          <p className="font-medium">
            {destinationLabel ?? <span className="text-gray-400">—</span>}
          </p>
        </div>
        <div>
          <span className="text-gray-500">
            <T context="sales order inline source label">Source</T>
          </span>
          <p className="font-medium">{salesOrder.source}</p>
        </div>
        <div>
          <span className="text-gray-500">
            <T context="sales order inline last synced label">Last synced</T>
          </span>
          <p className="font-medium">{formatDateTime(salesOrder.updatedAt)}</p>
        </div>
      </div>

      <div className="rounded-lg border border-green-300 bg-white overflow-hidden">
        <div className="max-h-[40vh] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-white [&_tr]:border-b-0 [&_th]:border-b [&_th]:border-gray-200 shadow-[0_1px_0_0_rgb(229_231_235)]">
              <TableRow>
                <TableHead className="w-12">
                  <T context="so line col line">Line</T>
                </TableHead>
                <TableHead>
                  <T context="so line col sku">SKU</T>
                </TableHead>
                <TableHead>
                  <T context="so line item description column">
                    Item Description
                  </T>
                </TableHead>
                <TableHead className="text-right">
                  <T context="so line col qty">Qty</T>
                </TableHead>
                <TableHead className="text-right">
                  <T context="so line col unit price">Unit</T>
                </TableHead>
                <TableHead className="text-right">
                  <T context="so line col total">Total</T>
                </TableHead>
                <TableHead>
                  <T context="so line col supplier">Supplier</T>
                </TableHead>
                <TableHead>
                  <T context="so line col delivery">Delivery</T>
                </TableHead>
                <TableHead>
                  <T context="so line col unit assignment">Unit</T>
                </TableHead>
                <TableHead>
                  <T context="so line col continuity">Continuity</T>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineItems.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="py-6 text-center text-sm text-gray-500"
                  >
                    <T context="so line empty">No line items.</T>
                  </TableCell>
                </TableRow>
              ) : (
                // Pin the trigger line (the one the suggestion was raised
                // for) to the top so the buyer sees it without scrolling.
                [...lineItems]
                  .sort((a, b) => {
                    const aHit = a.id === highlightLineItemId ? 0 : 1;
                    const bHit = b.id === highlightLineItemId ? 0 : 1;
                    if (aHit !== bHit) return aHit - bHit;
                    return 0;
                  })
                  .map((li) => (
                    <SalesOrderLineRow
                      key={li.id}
                      lineItemNumber={li.lineItemNumber}
                      // Resolved server-side from the v3 catalog (the only
                      // product an AS400 line has), so no v2 fallback chain.
                      sku={li.productSku}
                      productName={li.productName}
                      quantity={li.quantity}
                      unitPrice={li.unitPrice}
                      lineTotal={li.lineTotal}
                      isTrigger={li.id === highlightLineItemId}
                      ctx={replenishmentByLineItemId.get(li.id)}
                      salesOrderId={salesOrderId}
                      line={li}
                    />
                  ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
